/**
 * 画面別ヘルプ（B2 contextual help）の path → マニュアル章 対応。
 *
 * anchor は `docs/manual/user-manual.md` の見出しに rehype-slug が付与する id
 * （github-slugger 準拠）と一致させるため、Header 側の anchor 生成と HelpPage 側の
 * 見出し id 生成を同じ GithubSlugger で行う（ハードコードした文字列のズレを防ぐ）。
 *
 * 英語表示 (manual-english-20260924 / ADR-081) では英語版マニュアル
 * (`docs/manual/user-manual.en.md`) を表示するため、path → 章は**章番号**で引き、
 * 表示中の言語の H2 見出しから anchor を作る。
 */
import GithubSlugger from 'github-slugger';
import type { Locale } from '@/i18n';

/** 見出しテキスト → rehype-slug と同一規則の anchor slug を生成する */
export function slugify(headingText: string): string {
  // GithubSlugger はインスタンスで重複連番を管理するが、単一見出しの slug は
  // 先頭出現と同じになる（マニュアル H2 は全章ユニーク）。毎回新規生成で決定的にする。
  return new GithubSlugger().slug(headingText);
}

/** 目次見出し（fallback 用） */
const TOC_HEADING = '目次';

/**
 * path（先頭セグメント）→ 対応するマニュアル章見出し。
 * 見出し文字列は user-manual.md の H2 と一致させること。
 */
const PATH_TO_HEADING: Record<string, string> = {
  '/': '3. ダッシュボード',
  '/clients': '4. クライアント管理',
  '/client-groups': '4. クライアント管理',
  '/cases': '5. 案件管理',
  '/household-budgets': '5. 案件管理',
  // steering 464 B2: 入金管理 6 画面の解説は 5.7（第 5 章配下）
  '/payments': '5. 案件管理',
  // steering 464 B0-5: カレンダーの解説は 6.5（第 6 章配下）にあるため 5 章ではなく 6 章へ送る
  '/calendar': '6. 資料回収',
  '/collections': '6. 資料回収',
  '/templates': '6. 資料回収',
  '/doc-master': '6. 資料回収',
  // steering 464 B0-5: レポートの解説は 3.1（第 3 章配下）。未登録だと目次に落ちていた
  '/reports': '3. ダッシュボード',
  '/case-patrol': '3. ダッシュボード',
  '/vault': '7. 資料庫',
  '/chatwork': '9. Chatwork 連携',
  '/line': '10. LINE 連携',
  // steering 464 B3: メッセージ対応 workspace は第 16 章
  '/message-workspace': '16. メッセージ対応',
  '/messages': '12. 管理機能',
  // steering 463: 組織設定の canonical URL。先頭セグメント fallback でも解決するが意図を固定する
  '/settings/org': '12. 管理機能',
  '/settings': '12. 管理機能',
  '/admin': '12. 管理機能',
  '/platform': '13. プラットフォーム管理（Super Admin）',
  '/preferences': '2. ログイン・認証',
  '/bug-report': '14. トラブルシューティング',
  // manual-sync-20260907: 目次へ落ちていた画面に章を対応付ける
  '/client-tasks': '18. タスク管理',
  '/ai-connections': '19. AIと連携（コネクタ）',
};

/** 目次の章 key (`chapterKeyOfHeading` の値) */
const TOC_CHAPTER_KEY = 'toc';

/**
 * 英語版マニュアルの H2 見出し (章 key → 見出し)。
 * `docs/manual/user-manual.en.md` の H2 と一致させる (helpManualIntegrity.test.ts が突き合わせる)。
 * 英語版のファイルは HelpPage が英語表示のときだけ動的に読むため、Header から使う見出しはここに持つ。
 */
export const EN_CHAPTER_HEADINGS: Readonly<Record<string, string>> = {
  toc: 'Contents',
  '1': '1. Introduction',
  '2': '2. Sign-in and authentication',
  '3': '3. Dashboard',
  '4': '4. Client management',
  '5': '5. Case management',
  '6': '6. Document collection',
  '7': '7. Document vault',
  '8': '8. Client portal',
  '9': '9. Chatwork integration',
  '10': '10. LINE integration',
  '11': '11. Notifications and reminders',
  '12': '12. Administration',
  '13': '13. Platform management (Super Admin)',
  '14': '14. Troubleshooting',
  '15': '15. Glossary',
  '16': '16. Messages',
  '17': '17. Internal tags',
  '18': '18. Tasks',
  '19': '19. AI integration (connectors)',
};

/**
 * H2 見出しから章 key を作る (「5. 案件管理」→ '5'、目次 / Contents → 'toc')。
 * `scripts/check-manual-en-drift.mjs` の章の区切りと同じ規則。
 */
export function chapterKeyOfHeading(heading: string): string {
  const m = heading.match(/^(\d+)\./);
  if (m) return m[1];
  if (heading === TOC_HEADING || heading === EN_CHAPTER_HEADINGS[TOC_CHAPTER_KEY]) {
    return TOC_CHAPTER_KEY;
  }
  return heading;
}

/** 現在の pathname から対応する日本語版の章見出しを返す（なければ目次） */
function resolveJaHelpHeading(pathname: string): string {
  if (PATH_TO_HEADING[pathname]) return PATH_TO_HEADING[pathname];
  const seg = '/' + (pathname.split('/').filter(Boolean)[0] ?? '');
  return PATH_TO_HEADING[seg] ?? TOC_HEADING;
}

/**
 * 現在の pathname から、表示言語のマニュアルで対応する章見出しを返す（なければ目次）。
 * 英語は章番号で英語版の見出しを引く (英語版に無い章は英語版の目次へ)。
 */
export function resolveHelpHeading(pathname: string, locale: Locale = 'ja'): string {
  const ja = resolveJaHelpHeading(pathname);
  if (locale === 'ja') return ja;
  return EN_CHAPTER_HEADINGS[chapterKeyOfHeading(ja)] ?? EN_CHAPTER_HEADINGS[TOC_CHAPTER_KEY];
}

/** 現在の pathname から /help の anchor slug を返す（fallback は目次の slug） */
export function resolveHelpAnchor(pathname: string, locale: Locale = 'ja'): string {
  return slugify(resolveHelpHeading(pathname, locale));
}
